import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const PLONE_HOSTS = new Set([
  'clinicasantjosep.cat',
  'www.clinicasantjosep.cat',
  'api.clinicasantjosep.cat',
  'api.pre.clinicasantjosep.cat',
  'www.pre.clinicasantjosep.cat',
]);

const stripPlonePathPrefixes = (path) => {
  if (path.startsWith('/++api++/Plone')) {
    return path.slice('/++api++/Plone'.length) || '/';
  }
  if (path.startsWith('/Plone')) {
    return path.slice('/Plone'.length) || '/';
  }
  return path;
};

export const isPloneContentUrl = (value) => {
  if (!value || value.startsWith('mailto:') || value.startsWith('tel:')) {
    return false;
  }
  if (value.startsWith('/') && !value.startsWith('//')) {
    return false;
  }
  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      const { hostname, port } = new URL(value);
      if (hostname === 'localhost') {
        return port === '8080';
      }
      return PLONE_HOSTS.has(hostname);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Normalize Plone REST @id / API URLs to Volto app paths (/ca/...).
 * Handles api.pre / www.pre hosts that flattenToAppURL does not strip.
 */
export const normalizeAppUrl = (url) => {
  if (!url) {
    return url;
  }

  let normalized = flattenToAppURL(url);

  if (normalized.startsWith('/') && !normalized.startsWith('//')) {
    return stripPlonePathPrefixes(normalized);
  }

  if (isPloneContentUrl(normalized)) {
    try {
      const parsed = new URL(normalized);
      return stripPlonePathPrefixes(parsed.pathname || '/');
    } catch {
      return normalized;
    }
  }

  return normalized;
};

export const appLinkTo = (url, suffix = '') => {
  const normalized = normalizeAppUrl(url);
  const base = normalized === '' ? '/' : normalized;
  return `${base}${suffix}`;
};

export const isExternalAppUrl = (url) => {
  const normalized = normalizeAppUrl(url);
  return (
    normalized &&
    (normalized.startsWith('http://') || normalized.startsWith('https://'))
  );
};

/**
 * Build a Plone REST API URL from a path or absolute Plone @id.
 * Normalizes api.pre absolute URLs before adding apiPath and ++api++ suffix.
 */
export const buildPloneApiUrl = (
  path,
  { apiPath, apiSuffix = '/++api++', stripSubpathPrefixFn = (value) => value },
) => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    if (!isPloneContentUrl(path)) {
      return path;
    }
    const queryIndex = path.indexOf('?');
    const query = queryIndex >= 0 ? path.slice(queryIndex) : '';
    path = `${normalizeAppUrl(path)}${query}`;
  }

  const contentPath = stripSubpathPrefixFn(path[0] !== '/' ? `/${path}` : path);
  return `${apiPath}${apiSuffix}${contentPath}`;
};

/** @deprecated Use normalizeAppUrl */
export const normalizeNavUrl = normalizeAppUrl;

/** @deprecated Use appLinkTo */
export const navLinkTo = (url) => appLinkTo(url);

/** @deprecated Use isExternalAppUrl */
export const isExternalNavUrl = isExternalAppUrl;
